import _ from 'underscore';
import ko from 'knockout';
import { KnockoutView } from '@bingads-webui/knockout-view';
import { helpers } from '@bingads-webui/deterministic-view';
import { restoreScrollBarPosition } from '@bingads-webui/restore-scroll-bar';

import DraggableListTemplate from './template.pug';

const { updateView, ensureChildView } = helpers;

const KEYS = {
  SpaceBar: ' ',
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowRight: 'ArrowRight',
};

const scrollPixelSpeed = 3;

function rotate(contents, indexFrom, indexTo, tempState = false) {
  const contentsToUse = contents || [];
  const indexFromToUse = indexFrom || 0;
  const indexToToUse = indexTo || 0;
  const content = _.defaults({
    tempState,
  }, contentsToUse[indexFromToUse]);

  if (indexFromToUse < indexToToUse) {
    return _.flatten([
      contentsToUse.slice(0, indexFromToUse),
      contentsToUse.slice(indexFromToUse + 1, indexToToUse + 1),
      content,
      contentsToUse.slice(indexToToUse + 1),
    ]);
  }

  if (indexFromToUse > indexToToUse) {
    return _.flatten([
      contentsToUse.slice(0, indexToToUse),
      content,
      contentsToUse.slice(indexToToUse, indexFromToUse),
      contentsToUse.slice(indexFromToUse + 1),
    ]);
  }

  return contents;
}

class DraggableListViewModel {
  constructor() {
    this.draggingTo = ko.observable(-1);
  }
}

/**
 * The draggable list component
 *
 * @class DraggableList
 * @param {Object} options - DraggableList view config
 * @param {Object} options.itemViewFactory -
 *    The factory method or constructor of the draggable list child view
 *    - If the prototype of itemViewFactory is instance of DeterministicView, treat
 *      it as a construstor.
 *    - Otherwise, treat it as a factory method.
 * @param {fuction} options.afterDrop - a optional callback function to run after dropping
 * @param {fuction} options.getAnnouncement - an optional callback function to get announcement
 *                                            for a11y reading
 */
export class DraggableList extends KnockoutView {
  initialize(options) {
    super.initialize(options);
    this.uniqueId = _.uniqueId('draggable-list-');
    this.itemViewFactory = options.itemViewFactory;
    this.afterDrop = options.afterDrop || _.noop;
    this.ariaLabel = options.ariaLabel;
    this.getAnnouncement = options.getAnnouncement || _.noop;
    restoreScrollBarPosition(this, '.draggable-list');
    this.documentDragoverScrollCallback = this.documentDragoverScrollCallback.bind(this);
  }

  get ViewModel() { return DraggableListViewModel; }

  get className() {
    return 'draggable-list-view';
  }

  get template() { return DraggableListTemplate; }

  get events() {
    return {
      'dragover .draggable-list__item-draggable': 'onDragover',
      'dragstart .draggable-list__item-draggable': 'onDragstart',
      'drop .draggable-list__item-draggable': 'onDrop',
      'dragend .draggable-list__item-draggable': 'onDragEnd',
      'mousedown .draggable-list__item-draggable': 'onMouseDown',
      'keydown .draggable-list__item-draggable': 'onKeyDown',
      'focusRequestFromChildren .draggable-list__item-draggable': 'onFocusRequestFromChildren',
    };
  }

  setState({
    lockedItems = [],
    draggableItems = [],
    draggingTo = -1,
  } = {}) {
    _.each(lockedItems, (config, index) => {
      ensureChildView(this, `locked-list-item-${index}`, this.itemViewFactory).setState(config);
    });
    _.each(draggableItems, (config, index) => {
      ensureChildView(this, `draggable-list-item-${index}`, this.itemViewFactory).setState(config);
    });

    this.model.set({
      lockedItemsLength: lockedItems.length,
      draggableItemsLength: draggableItems.length,
      uniqueId: this.uniqueId,
    });

    this.viewModel.draggingTo(draggingTo);

    return this;
  }

  getState() {
    const lockedItemsLength = this.model.get('lockedItemsLength');
    const draggableItemsLength = this.model.get('draggableItemsLength');

    const lockedItems = _.times(lockedItemsLength, index => this.getChild(`locked-list-item-${index}`).getState());
    const draggableItems = _.times(draggableItemsLength, index => this.getChild(`draggable-list-item-${index}`).getState());

    return {
      lockedItems,
      draggableItems,
    };
  }

  /**
   * When dragging item out of the list, the list should scroll if possiable.
   * detect the relative position of mouse and the list.
   *
   * @param {DragEvent} e - drag event
   * @returns {void}
   */
  documentDragoverScrollCallback(e) {
    const listElement = this.el.querySelector('.draggable-list');
    const domRect = listElement.getBoundingClientRect();

    const mousePageY = e.pageY;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const elementPageTop = domRect.top + scrollTop;
    const elementPageBottom = domRect.bottom + scrollTop;

    if (mousePageY <= elementPageTop) {
      this.scrollStart({ top: -scrollPixelSpeed });
    } else if (mousePageY >= elementPageBottom) {
      this.scrollStart({ top: scrollPixelSpeed });
    } else {
      this.scrollStop();
    }
  }

  onDragstart(e) {
    if (e.originalEvent.dataTransfer) {
      // firefox need to setData to make drag work
      e.originalEvent.dataTransfer.setData('Text', `item:${e.target.dataset.index}` || 'item:0');
    }

    this.draggableItems = this.getState().draggableItems;
    this.indexFrom = parseInt(e.target.dataset.index, 10);
    this.scrollDetectStart();
  }

  get isDragging() {
    return this.indexFrom !== undefined;
  }

  onDragover(e) {
    e.preventDefault();
    e.stopPropagation();
    const targetItem = this.$(e.target).closest(`ul#${this.uniqueId} > li.draggable-list__item-draggable`)[0];
    if (targetItem) {
      const indexTo = parseInt(targetItem.dataset.index, 10);
      updateView(this, {
        draggableItems: rotate(this.draggableItems, this.indexFrom, indexTo, true),
        draggingTo: indexTo,
      });
    }
  }

  onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const targetItem = this.$(e.target).closest(`ul#${this.uniqueId} > li.draggable-list__item-draggable`)[0];
    if (targetItem) {
      const indexTo = parseInt(targetItem.dataset.index, 10);
      updateView(this, {
        draggableItems: rotate(this.draggableItems, this.indexFrom, indexTo),
      });
      this.moveFocusTo(indexTo, this.indexFrom);
    }

    delete this.indexFrom;
    delete this.draggableItems;
  }

  onDragEnd(e) {
    e.preventDefault();
    e.stopPropagation();
    this.viewModel.draggingTo(-1);
    this.scrollDetectStop();
    this.afterDrop();
  }

  onMouseDown(e) {
    const targetItem = this.$(e.currentTarget);

    if (targetItem.attr('tabindex') !== '0') {
      this.indexFrom = -1;

      this.$(`ul#${this.uniqueId} > li.draggable-list__item-draggable[class*='selected']`).removeClass('selected').removeAttr('aria-selected');
      this.$(`ul#${this.uniqueId} > li.draggable-list__item-draggable[tabindex='0']`).attr('tabindex', '-1');
      targetItem.attr('tabindex', '0');
    }
  }

  /**
   * control list to scroll
   *
   * @param {ScrollToOptions} options scroll options
   * @returns {void}
   */
  scroll(options) {
    const listElement = this.el.querySelector('.draggable-list');
    listElement.scrollBy(options);
  }

  /**
   * start scroll for every `intervalMilliSeconds` ms if no scrolling.
   *
   * @param {ScrollToOptions} options scroll options
   * @param {number} intervalMilliSecond how long before next scroll, default is 16
   * @returns {void}
   */
  scrollStart(options, intervalMilliSecond = 16) {
    if (this.scrollTimerId !== undefined) {
      return;
    }
    const timerId = setInterval(() => {
      this.scroll(options);
    }, intervalMilliSecond);
    this.scrollTimerId = timerId;
  }

  scrollStop() {
    if (this.scrollTimerId) {
      clearInterval(this.scrollTimerId);
      this.scrollTimerId = undefined;
    }
  }

  scrollDetectStart() {
    document.addEventListener('dragover', this.documentDragoverScrollCallback);
  }

  scrollDetectStop() {
    this.scrollStop();
    document.removeEventListener('dragover', this.documentDragoverScrollCallback);
  }

  /**
   * If children bubble this event, focus on its parent list-item.
   *
   * @param {KeyboardEvent} e - custom event
   * @returns {void}
   */
  onKeyDown(e) {
    if (e.target !== e.currentTarget) {
      return;
    }

    const acceptableKeysReg = new RegExp(`(${Object.values(KEYS).join('|')})`);
    const triggerKey = e.key;
    if (!acceptableKeysReg.test(triggerKey)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const targetItem = this.$(e.target);
    const index = parseInt(e.target.dataset.index, 10);
    switch (triggerKey) {
      case KEYS.SpaceBar:
        if (targetItem.hasClass('selected')) {
          targetItem.removeClass('selected');
          targetItem.removeAttr('aria-selected');
          _.defer(() => {
            this.announce({
              action: 'DropItem',
              positionFrom: this.indexFrom,
              positionTo: index,
            });
            this.indexFrom = -1;
          });
        } else {
          this.indexFrom = index;
          targetItem.addClass('selected');
          targetItem.attr('aria-selected', 'true');
          _.defer(() => this.announce({
            action: 'LiftItem',
            positionFrom: this.indexFrom,
          }));
        }
        break;
      case KEYS.ArrowDown:
        this.draggableItems = this.getState().draggableItems;
        if (index < this.draggableItems.length - 1) {
          if (this.indexFrom >= 0) {
            updateView(this, {
              draggableItems: rotate(this.draggableItems, index, index + 1),
            });
            this.moveFocusTo(index + 1, index, true);
            _.defer(() => this.announce({
              action: 'MoveItem',
              positionFrom: this.indexFrom,
              positionTo: index + 1,
            }));
          } else {
            this.moveFocusTo(index + 1, index);
          }
        }
        break;
      case KEYS.ArrowUp:
        this.draggableItems = this.getState().draggableItems;
        if (index > 0) {
          if (this.indexFrom >= 0) {
            updateView(this, {
              draggableItems: rotate(this.draggableItems, index, index - 1),
            });
            this.moveFocusTo(index - 1, index, true);
            _.defer(() => this.announce({
              action: 'MoveItem',
              positionFrom: this.indexFrom,
              positionTo: index - 1,
            }));
          } else {
            this.moveFocusTo(index - 1, index);
          }
        }
        break;
      case KEYS.ArrowRight:
        this.getListItems()
          .eq(index)
          .find('.focusable')
          .trigger('focus');
        break;
      default:
        break;
    }
  }

  /**
   * If children bubble this event, focus on its parent list-item.
   *
   * @param {Event} e - custom event
   * @returns {void}
   */
  onFocusRequestFromChildren(e) {
    e.preventDefault();
    e.stopPropagation();
    const index = parseInt(e.currentTarget.dataset.index, 10);
    this.moveFocusTo(index);
  }

  getListItems() {
    return this.$(`ul#${this.uniqueId} > li.draggable-list__item-draggable`);
  }

  moveFocusTo(indexTo, indexFrom, selected = false) {
    const items = this.getListItems();
    if (indexFrom !== undefined) {
      const itemFrom = items.eq(indexFrom);
      itemFrom.attr('tabindex', '-1');
      if (selected) {
        itemFrom.removeClass('selected');
        itemFrom.removeAttr('aria-selected');
      }
    }
    const itemTo = items.eq(indexTo);
    if (selected) {
      itemTo.addClass('selected');
      itemTo.attr('aria-selected', 'true');
    }
    itemTo.attr('tabindex', '0');
    itemTo.trigger('focus');
  }

  announce({
    action,
    positionFrom,
    positionTo,
  }) {
    const liveRegion = this.$(`ul#${this.uniqueId} + .live-region`);
    liveRegion.text(this.getAnnouncement({
      action,
      positionFrom,
      positionTo,
    }));
  }
}
